import React, { useEffect, useRef, useState } from 'react'
import { ChevronDownIcon, ChevronUpIcon, CheckIcon } from '@heroicons/react/24/outline'
import { useLoadboardTheme } from './loadboardTheme'
import { Label, makeInputBase } from './FilterFields'

const MIN_HOURS = 1 / 60
const MAX_HOURS = 24

const PRESETS = [
	{ hours: 1 / 60, label: '1 min' },
	{ hours: 10 / 60, label: '10 mins' },
	{ hours: 30 / 60, label: '30 mins' },
	{ hours: 1, label: '1 hour' },
	{ hours: 4, label: '4 hours' },
	{ hours: 10, label: '10 hours' },
	{ hours: 24, label: '24 hours' },
]

const parseInput = raw => {
	if (typeof raw !== 'string') return null
	const s = raw.trim().toLowerCase()
	if (!s) return null
	const m = s.match(/^(\d+(?:\.\d+)?)\s*(m|min|mins|h|hr|hrs|hour|hours|d|day|days)?$/)
	if (!m) return null
	const n = Number(m[1])
	if (!Number.isFinite(n) || n <= 0) return null
	const unit = m[2] || 'm'
	if (unit.startsWith('m')) return n / 60
	if (unit.startsWith('d')) return n * 24
	return n
}

const formatTrigger = hours => {
	if (hours == null || hours === '') return 'Any'
	const h = Number(hours)
	if (!Number.isFinite(h) || h <= 0) return 'Any'
	if (h < 1) {
		const mins = Math.round(h * 60)
		return `${mins} ${mins === 1 ? 'min' : 'mins'}`
	}
	if (h % 24 === 0) {
		const days = h / 24
		return `${days} ${days === 1 ? 'day' : 'days'}`
	}
	const rounded = Number(h.toFixed(2))
	return `${rounded} ${rounded === 1 ? 'hr' : 'hrs'}`
}

function LastSeenDropdown({ value, onChange }) {
	const { theme, tokens } = useLoadboardTheme()
	const isLight = theme === 'light'
	const inputBase = makeInputBase(tokens)
	const [isOpen, setIsOpen] = useState(false)
	const [customText, setCustomText] = useState('')
	const wrapRef = useRef(null)

	useEffect(() => {
		if (!isOpen) return
		const onClick = e => {
			if (wrapRef.current && !wrapRef.current.contains(e.target)) {
				const parsed = parseInput(customText)
				if (parsed != null) {
					const clamped = Math.min(MAX_HOURS, Math.max(MIN_HOURS, parsed))
					onChange(String(clamped))
					setCustomText('')
				}
				setIsOpen(false)
			}
		}
		document.addEventListener('mousedown', onClick)
		return () => document.removeEventListener('mousedown', onClick)
	}, [isOpen, customText, onChange])

	const numericValue = value === '' || value == null ? null : Number(value)
	const selectedPreset = PRESETS.find(p => p.hours === numericValue) || null

	const commit = next => {
		if (next == null) {
			onChange('')
			return
		}
		const clamped = Math.min(MAX_HOURS, Math.max(MIN_HOURS, next))
		onChange(String(clamped))
	}

	const onCustomKey = e => {
		if (e.key !== 'Enter') return
		const parsed = parseInput(customText)
		if (parsed == null) return
		commit(parsed)
		setCustomText('')
		setIsOpen(false)
	}

	const panelBg = isLight
		? 'bg-white ring-1 ring-gray-200 shadow-lg'
		: 'bg-[#15202b] ring-1 ring-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.4)]'
	const activeRowCls = isLight
		? 'bg-sky-50 text-sky-700'
		: 'bg-sky-500/15 text-sky-100'
	const inactiveRowCls = isLight
		? 'text-gray-800 hover:bg-gray-50'
		: 'text-gray-100 hover:bg-white/5'
	const helperCls = isLight ? 'text-gray-500' : 'text-gray-500'
	const customInputCls = isLight
		? 'bg-gray-50 ring-1 ring-gray-200 text-gray-900 placeholder-gray-400'
		: 'bg-white/[0.04] ring-1 ring-white/10 text-gray-100 placeholder-gray-500'

	return (
		<div ref={wrapRef} className="w-full relative">
			<div className={tokens.slimFieldShell}>
				<Label>Last seen</Label>
				<button
					type="button"
					onClick={() => setIsOpen(v => !v)}
					className={`${inputBase} appearance-none cursor-pointer flex items-center justify-between text-left pr-5 h-[22px] leading-[22px]`}
				>
					<span className="truncate">{formatTrigger(value)}</span>
				</button>
				{isOpen ? (
					<ChevronUpIcon className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
				) : (
					<ChevronDownIcon className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
				)}
			</div>

			{isOpen && (
				<div className={`absolute left-0 top-full mt-2 z-30 w-44 rounded-lg p-2.5 ${panelBg}`}>
					<div className={`flex items-center gap-2 rounded-md px-2.5 py-1.5 ${customInputCls}`}>
						<input
							type="text"
							value={customText}
							onChange={e => setCustomText(e.target.value)}
							onBlur={() => {
								const parsed = parseInput(customText)
								if (parsed != null) commit(parsed)
							}}
							onKeyDown={onCustomKey}
							placeholder="e.g. 40 (mins), 2h"
							className="w-full bg-transparent outline-none text-[12px]"
						/>
					</div>
					<div className="h-2" />

					<div className="max-h-64 overflow-y-auto">
						<button
							type="button"
							onClick={() => {
								commit(null)
								setIsOpen(false)
							}}
							className={`w-full flex items-center justify-between gap-2 rounded-md px-2 py-0.5 text-[13px] ${
								numericValue == null ? activeRowCls : inactiveRowCls
							}`}
						>
							<span className="font-semibold">Any</span>
							{numericValue == null && <CheckIcon className="h-3.5 w-3.5 text-sky-400" />}
						</button>

						{PRESETS.map(p => {
							const isActive = selectedPreset?.hours === p.hours
							return (
								<button
									type="button"
									key={p.hours}
									onClick={() => {
										commit(p.hours)
										setIsOpen(false)
									}}
									className={`w-full flex items-center justify-between gap-2 rounded-md px-2 py-0.5 text-[13px] ${
										isActive ? activeRowCls : inactiveRowCls
									}`}
								>
									<span className="font-semibold">{p.label}</span>
									{isActive && <CheckIcon className="h-3.5 w-3.5 text-sky-400" />}
								</button>
							)
						})}
					</div>
				</div>
			)}
		</div>
	)
}

export default LastSeenDropdown
